import { useLayoutEffect } from 'react';
import { useQueryClient, useJobStatus } from 'shared-ui';
import { toast } from 'react-toastify';
import { yupResolver } from '@hookform/resolvers/yup';
import { FormProvider, useForm } from 'react-hook-form';

import { useLocale } from 'hooks/useLocale/useLocale';
import { QueryKeyEnum } from 'core/global.enum';
import { AppMessages } from 'i18n/messages';
import { useSelectedGroup } from 'hooks/useSelectedGroup/useSelectedGroup';
import { Group, UpdateGroupBody } from 'api/groups/groups.types';
import { updateGroup } from 'api/groups/groups';
import { useMutation } from 'hooks/useMutation/useMutation';
import { AppRouteWithParams } from 'routing/AppRoute.enum';
import { useIsExactPath } from 'hooks/useIsExactPath/useIsExactPath';
import { isJobResponse } from '../utils/groupUpdateHelpers';

import { GroupDetails } from './GroupDetails';
import { GroupDetailsContainerProps } from './GroupDetails.types';
import { useGroupDetailsValidation } from './GroupDetails.validation';

export const GroupDetailsContainer = ({ selectedGroup }: GroupDetailsContainerProps) => {
  const { formatMessage } = useLocale();
  const { setSelectedGroup } = useSelectedGroup();
  const { schema } = useGroupDetailsValidation();
  const queryClient = useQueryClient();
  const isHistoryView = useIsExactPath(AppRouteWithParams.groupsHistory.path);

  const handleJobComplete = () => {
    if (isHistoryView) {
      queryClient.invalidateQueries([QueryKeyEnum.GROUP_HISTORY]);
    } else {
      queryClient.invalidateQueries([QueryKeyEnum.GROUPS]);
    }

    queryClient.invalidateQueries([QueryKeyEnum.GROUP_DETAILS, selectedGroup.id]);

    toast.dark(formatMessage({ id: AppMessages['group.details.success'] }));
  };

  const jobStatus = useJobStatus({
    autoClose: false,
    useGlobalModal: true,
    customCompleteMessage: formatMessage({ id: AppMessages['groups.result.success'] }),
    customFailedMessage: formatMessage({ id: AppMessages['groups.result.failed'] }),
    customActiveMessage: formatMessage({ id: AppMessages['groups.result.waiting'] }),
    showItemCount: false,
    onComplete: handleJobComplete,
  });

  const { mutate: onUpdateOwner, isLoading } = useMutation(updateGroup, {
    onSuccess: (response, { body }) => {
      if (isJobResponse(response)) {
        jobStatus.startJob(
          response.jobId,
          formatMessage({ id: AppMessages['job.status.modal.updating.group.permissions'] }),
        );
      } else {
        const updatedGroup = { ...selectedGroup, ...body };
        setSelectedGroup(updatedGroup);
        formMethods.reset(updatedGroup);

        if (isHistoryView) {
          queryClient.invalidateQueries([QueryKeyEnum.GROUP_HISTORY]);
        } else {
          queryClient.invalidateQueries([QueryKeyEnum.GROUPS]);
        }

        toast.dark(formatMessage({ id: AppMessages['group.details.success'] }));
      }
    },
  });

  const formMethods = useForm<UpdateGroupBody>({
    mode: 'all',
    defaultValues: selectedGroup,
    resolver: yupResolver(schema),
  });

  useLayoutEffect(() => {
    formMethods.reset(selectedGroup);
  }, [selectedGroup]);

  useLayoutEffect(() => {
    setSelectedGroup(selectedGroup);
  }, []);

  const onSubmit = (body: Group) => {
    onUpdateOwner({ id: selectedGroup.id, body: { ...selectedGroup, ...body } });
  };

  return (
    <FormProvider {...formMethods}>
      <GroupDetails selectedGroup={selectedGroup} onSubmit={onSubmit} isLoading={isLoading} />
    </FormProvider>
  );
};
